import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { validateDeeplinkToken } from 'services/apiService';

// ==============================|| DEEPLINK HANDLER ||============================== //

const DeeplinkHandler = () => {
  const [searchParams] = useSearchParams();
  const { organizationName: routeOrganizationName } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = searchParams.get('token');

  useEffect(() => {
    let cancelled = false;

    const validate = async () => {
      try {
        setLoading(true);

        if (!token) {
          // Clear any stale session when token missing
          sessionStorage.clear();
          localStorage.removeItem('org_name');
          if (!cancelled) {
            setError('Invalid deeplink: No token provided');
          }
          return;
        }

        const response = await validateDeeplinkToken(token);

        if (cancelled) return;

        if (response && response.data && response.data.valid) {
          const { auth_token, user, device_imei, organization_name } = response.data;

          // Store auth token and user info in sessionStorage (NOT localStorage)
          sessionStorage.setItem('auth_token', auth_token);
          sessionStorage.setItem('user', JSON.stringify(user));
          sessionStorage.setItem('device_imei', device_imei);
          sessionStorage.setItem('is_partner_session', 'true');

          // Prefer backend-provided organization, then URL org segment, then safe default.
          const orgName = (organization_name || routeOrganizationName || 'smart-monitor').toLowerCase();
          localStorage.setItem('org_name', orgName);

          // SPA navigation
          navigate(`/${orgName}/device-info`, { replace: true });
        } else {
          // Clear any stale session data on failure
          sessionStorage.clear();
          localStorage.removeItem('org_name');
          if (!cancelled) {
            setError('Token validation failed. Please request a new link.');
          }
        }
      } catch (err) {
        // Clear any stale session data on error
        sessionStorage.clear();
        localStorage.removeItem('org_name');
        const errorMessage = err?.response?.data?.message || err?.message || 'Token expired, already used, or invalid';
        if (!cancelled) {
          setError(errorMessage);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    validate();

    return () => {
      cancelled = true;
    };
  }, [token, navigate, routeOrganizationName]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 3
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h5" color="text.secondary">
          Validating session...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we verify your access
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3
        }}
      >
        <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
          ❌
        </Typography>
        <Typography variant="h4" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Alert severity="error" sx={{ maxWidth: 600, mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 500 }}>
          This link may have expired or already been used. Each access link can only be used once and expires after 2 minutes.
        </Typography>
      </Box>
    );
  }

  return null;
};

export default DeeplinkHandler;

